(() => {
  const instances = new WeakMap();

  class RemanxCatalogue {
    constructor(root) {
      this.root = root;
      this.section = root.closest('.shopify-section') || root.parentElement;
      this.toolbar = root.querySelector('.rmx-toolbar-shell > products-toolbar');
      this.mobileTrigger = root.querySelector('.rmx-mobile-filter-trigger');
      this.mobileCount = root.querySelector('.rmx-mobile-filter-count');
      this.activeWrap = root.querySelector('.rmx-toolbar-active-filters');
      this.activeRail = root.querySelector('.rmx-toolbar-active-filters__rail');
      this.activeReset = root.querySelector('.rmx-toolbar-active-filters__reset');
      this.pagination = root.querySelector('custom-pagination');
      this.filterLabels = new Map();
      this.frame = 0;
      this.heightFrame = 0;
      this.activeSignature = '';
      this.abort = new AbortController();
      this.readFilterLabels();
      this.bind();
      this.hydrate();
    }

    readFilterLabels() {
      this.root.querySelectorAll('[data-rmx-filter-param]').forEach((item) => {
        this.filterLabels.set(item.dataset.rmxFilterParam, item.dataset.rmxFilterLabel || 'Filter');
      });
    }

    bind() {
      const signal = this.abort.signal;
      this.root.addEventListener('click', (event) => this.onClick(event), { capture: true, signal });
      this.root.addEventListener('change', () => this.scheduleHydrate(), { signal });
      window.addEventListener('pageshow', () => this.scheduleHydrate(), { passive: true, signal });
      window.addEventListener('popstate', () => this.scheduleHydrate(), { passive: true, signal });

      this.observer = new MutationObserver(() => this.scheduleHydrate());
      this.observer.observe(this.root, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-layout','aria-expanded','open'] });
    }

    destroy() {
      this.abort.abort();
      this.observer?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      if (this.heightFrame) cancelAnimationFrame(this.heightFrame);
    }

    get nativeFilterTrigger() {
      return this.toolbar?.querySelector('.products-toolbar__filter-btn');
    }

    onClick(event) {
      const remove = event.target.closest('.rmx-toolbar-filter-chip__remove');
      if (remove && this.root.contains(remove)) {
        event.preventDefault();
        event.stopPropagation();
        const href = remove.dataset.removeUrl;
        if (href) window.location.assign(href);
        return;
      }

      if (event.target.closest('.rmx-mobile-filter-count')) {
        event.preventDefault();
        event.stopPropagation();
        window.location.assign(this.getResetUrl());
        return;
      }

      const mobileTrigger = event.target.closest('.rmx-mobile-filter-trigger');
      if (mobileTrigger && this.root.contains(mobileTrigger)) {
        event.preventDefault();
        const drawer = this.root.querySelector('facet-filters');
        if (drawer?.drawerIsOpen) drawer.close();
        else drawer?.open(mobileTrigger);
      }
    }

    scheduleHydrate() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => this.hydrate());
    }

    hydrate() {
      this.frame = 0;
      this.markNativeSort();
      this.syncMobileTrigger();
      this.renderActiveFilters();
      this.placeCardExtras();
      this.scheduleHeight();
    }

    markNativeSort() {
      if (!this.toolbar) return;
      this.toolbar.querySelectorAll('select[name="sort_by"]').forEach((select) => {
        let target = select;
        while (target.parentElement && target.parentElement !== this.toolbar) {
          const parent = target.parentElement;
          if (parent.querySelector('.products-toolbar__filter-btn,.products-toolbar__layout,.products-toolbar__compare')) break;
          target = parent;
        }
        target.classList.add('rmx-native-sort-control');
        select.tabIndex = -1;
        select.setAttribute('aria-hidden', 'true');
      });
    }

    syncMobileTrigger() {
      const native = this.nativeFilterTrigger;
      if (!native || !this.mobileTrigger) return;
      const expanded = native.getAttribute('aria-expanded') || 'false';
      if (this.mobileTrigger.getAttribute('aria-expanded') !== expanded) this.mobileTrigger.setAttribute('aria-expanded', expanded);
      if (native.getAttribute('aria-controls')) this.mobileTrigger.setAttribute('aria-controls', native.getAttribute('aria-controls'));
    }

    prettify(name) {
      const raw = name.replace(/^filter\.[pv]\./, '').replace(/\.(gte|lte)$/i, '').split('.').pop() || 'Filter';
      return raw.replace(/^m\.custom\./, '').replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    }

    isMakeFilterParam(name) {
      return /make/i.test(`${name} ${this.filterLabels.get(name) || ''}`);
    }

    isModelFilterParam(name) {
      return /model/i.test(`${name} ${this.filterLabels.get(name) || ''}`);
    }

    buildUrlWithout(names, value = null) {
      const url = new URL(window.location.href);
      const list = Array.isArray(names) ? names : [names];
      const removesMake = list.some((name) => this.isMakeFilterParam(name));

      list.forEach((name) => {
        if (value === null || list.length > 1) {
          url.searchParams.delete(name);
          return;
        }
        const remaining = url.searchParams.getAll(name).filter((item) => item !== value);
        url.searchParams.delete(name);
        remaining.forEach((item) => url.searchParams.append(name, item));
      });

      // A Model without a Make is ambiguous and can leave the catalogue at zero results.
      if (removesMake) {
        const stillHasMake = Array.from(url.searchParams.keys()).some((name) => this.isMakeFilterParam(name));
        if (!stillHasMake) {
          Array.from(url.searchParams.keys()).forEach((name) => {
            if (this.isModelFilterParam(name)) url.searchParams.delete(name);
          });
        }
      }

      return `${url.pathname}${url.search}${url.hash}`;
    }

    getResetUrl() {
      const url = new URL(window.location.href);
      [...url.searchParams.keys()].forEach((name) => {
        if (name.startsWith('filter.')) url.searchParams.delete(name);
      });
      return `${url.pathname}${url.search}${url.hash}`;
    }

    readActiveFilters() {
      const params = new URL(window.location.href).searchParams;
      const filters = [];
      const years = new Map();
      const prices = new Map();
      const currency = this.root.dataset.currencySymbol || '£';

      params.forEach((value, name) => {
        if (!name.startsWith('filter.')) return;
        const label = this.filterLabels.get(name) || this.prettify(name);
        if (/vehicle_fitments/i.test(name) || /^vehicle fitments?$/i.test(label)) return;

        if (/price/i.test(name) && /\.(gte|lte)$/i.test(name)) {
          const base = name.replace(/\.(gte|lte)$/i, '');
          const entry = prices.get(base) || { label, names: [], min: '', max: '' };
          entry.names.push(name);
          if (/\.gte$/i.test(name)) entry.min = value;
          if (/\.lte$/i.test(name)) entry.max = value;
          prices.set(base, entry);
          return;
        }

        if (/year/i.test(`${name} ${label}`)) {
          const entry = years.get(name) || { name, label, values: [] };
          entry.values.push(value);
          years.set(name, entry);
          return;
        }

        filters.push({ label, value, href: this.buildUrlWithout(name, value) });
      });

      years.forEach((entry) => {
        const numbers = entry.values.map((v) => Number.parseInt(v, 10)).filter(Number.isFinite).sort((a,b) => a-b);
        const value = numbers.length
          ? (numbers[0] === numbers[numbers.length - 1] ? String(numbers[0]) : `${numbers[0]}–${numbers[numbers.length - 1]}`)
          : entry.values.join(', ');
        filters.push({ label: entry.label || 'Year', value, href: this.buildUrlWithout(entry.name) });
      });

      prices.forEach((entry) => {
        const money = (value) => value === '' ? '' : `${currency}${value}`;
        let value = '';
        if (entry.min && entry.max) value = `${money(entry.min)}–${money(entry.max)}`;
        else if (entry.min) value = `From ${money(entry.min)}`;
        else if (entry.max) value = `Up to ${money(entry.max)}`;
        filters.push({ label: entry.label || 'Price', value, href: this.buildUrlWithout(entry.names) });
      });

      return filters;
    }

    renderActiveFilters() {
      const filters = this.readActiveFilters();
      const count = filters.length;
      const word = count === 1 ? 'filter' : 'filters';

      if (this.mobileCount) {
        this.mobileCount.hidden = count === 0;
        const text = this.mobileCount.querySelector('.rmx-mobile-filter-count__text');
        if (text) text.textContent = count ? `${count} ${word}` : '';
        this.mobileCount.setAttribute('aria-label', count ? `Clear ${count} active ${word}` : 'Clear active filters');
      }

      if (!this.activeWrap || !this.activeRail || !this.activeReset) return;
      this.activeWrap.hidden = count === 0;
      this.activeReset.href = this.getResetUrl();
      const signature = JSON.stringify(filters.map(({label,value,href}) => [label,value,href]));
      if (signature === this.activeSignature) return;

      this.activeRail.replaceChildren();
      filters.forEach((filter) => {
        const chip = document.createElement('span');
        chip.className = 'rmx-toolbar-filter-chip';
        chip.title = `${filter.label}: ${filter.value}`;
        const value = document.createElement('span');
        value.className = 'rmx-toolbar-filter-chip__value';
        value.textContent = filter.value;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'rmx-toolbar-filter-chip__remove';
        remove.dataset.removeUrl = filter.href;
        remove.textContent = '×';
        remove.setAttribute('aria-label', `Remove ${filter.label}: ${filter.value} filter`);
        chip.append(value, remove);
        this.activeRail.append(chip);
      });
      this.activeSignature = signature;
    }

    placeCardExtras() {
      const listMode = this.pagination?.dataset.layout === 'list';
      this.root.querySelectorAll('product-card').forEach((card) => {
        const quick = card.querySelector('.card__quick-add');
        if (quick) {
          const destination = listMode
            ? (card.querySelector('.card__info') || card.querySelector('.card__info-container'))
            : card.querySelector('.card__media');
          if (destination && quick.parentElement !== destination) destination.append(quick);
          card.classList.toggle('rmx-quick-add-in-row', listMode);
          card.classList.toggle('rmx-quick-add-in-media', !listMode);
        }
      });

      this.root.querySelectorAll('.rmx-card-desc').forEach((desc) => {
        const card = desc.closest('li')?.querySelector('product-card');
        const info = card?.querySelector('.card__info') || card?.querySelector('.card__info-container');
        if (info && desc.parentElement !== info) info.append(desc);
      });
    }

    scheduleHeight() {
      if (this.heightFrame) return;
      this.heightFrame = requestAnimationFrame(() => this.updateMeasurements());
    }

    updateMeasurements() {
      this.heightFrame = 0;

      // Keep the facet at its natural content height. CSS already provides a
      // viewport-based max-height as a safety cap, so an internal scrollbar
      // only appears when the expanded filters genuinely cannot fit on screen.
      // Do not resize the panel as it approaches the bottom of the product
      // grid/footer: changing its height during scroll makes the sticky release
      // feel clipped and turns the sidebar into a fixed-height scroll box.
      this.root.style.removeProperty('--rmx-filter-card-max-height');
      this.root.style.removeProperty('--rmx-filter-row-offset');
    }
  }

  const init = (scope = document) => {
    const roots = [];
    if (scope.matches?.('[data-rmx-catalogue]')) roots.push(scope);
    scope.querySelectorAll?.('[data-rmx-catalogue]').forEach((root) => roots.push(root));
    roots.forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new RemanxCatalogue(root));
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-rmx-catalogue]').forEach((root) => {
      instances.get(root)?.destroy();
      instances.delete(root);
    });
  });

  new MutationObserver((records) => {
    records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node.nodeType === 1) init(node);
    }));
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
